/**
 * 經銷商分潤估算。基準是客戶未稅實收，規則對齊經銷商分潤辦法，比例可覆寫。
 */

/** ChatMate、ChatDesk 不論等級，訂閱分潤固定 25% */
export const AI_TOKEN_COST_MODULE_CODES = ['chatmate', 'chatdesk'];
export const AI_MODULE_SHARE_RATE = 25;

/** 訂閱月費預設不分潤，仍可逐列改比例 */
export const NO_SHARE_MODULE_CODES = ['content_publishing', 'marketing_ops'];

/** 比照導入費／建置費分潤的項目（含加值版建置服務） */
export const SETUP_SHARE_CODES = ['setup_fee', 'premium_setup'];

const TIER_RATES = {
  standard: 30,
  advanced: 40
};

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function clampSharePercent(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, n));
}

export function defaultDealerShareParams() {
  return {
    tier: 'standard',
    subscriptionRate: TIER_RATES.standard,
    enterpriseDeal: 'dealer',
    referralRate: 20,
    setupExecutor: 'factory',
    setupDealerRate: 100,
    rateOverrides: {}
  };
}

export function normalizeDealerShareParams(raw) {
  const base = defaultDealerShareParams();
  if (!raw || typeof raw !== 'object') return base;
  const tier = raw.tier === 'advanced' || raw.tier === 'custom' ? raw.tier : 'standard';
  return {
    tier,
    subscriptionRate: clampSharePercent(raw.subscriptionRate, base.subscriptionRate),
    enterpriseDeal: raw.enterpriseDeal === 'referral' ? 'referral' : 'dealer',
    referralRate: clampSharePercent(raw.referralRate, base.referralRate),
    setupExecutor: raw.setupExecutor === 'dealer' ? 'dealer' : 'factory',
    setupDealerRate: clampSharePercent(raw.setupDealerRate, base.setupDealerRate),
    rateOverrides: raw.rateOverrides && typeof raw.rateOverrides === 'object'
      ? { ...raw.rateOverrides }
      : {}
  };
}

function moduleCodeFromItem(code) {
  const value = String(code || '');
  if (value === 'module_chatdesk_extra_seats') return null;
  if (!value.startsWith('module_')) return null;
  return value.slice('module_'.length);
}

function hasOverride(overrides, code) {
  const raw = overrides?.[code];
  return raw != null && raw !== '' && Number.isFinite(Number(raw));
}

function lineShare(basisAmount, rate) {
  if (basisAmount == null || !Number.isFinite(Number(basisAmount))) return null;
  return roundMoney(Number(basisAmount) * clampSharePercent(rate) / 100);
}

function pushShare(bucket, amount) {
  if (amount == null) return;
  bucket.total = roundMoney(bucket.total + amount);
}

/**
 * @param {object} quote calculateQuote 的結果
 * @param {object} params
 * @param {object} catalog
 */
export function calculateDealerShare(quote, params, catalog) {
  const settings = normalizeDealerShareParams(params);
  const priceMonths = Number(quote?.billingCycle?.priceMonths) || 1;
  const planCode = quote?.plan?.code || '';
  const referral = settings.enterpriseDeal === 'referral' && planCode === 'enterprise';
  const subscriptionRate = settings.subscriptionRate;
  const aiModules = new Set(AI_TOKEN_COST_MODULE_CODES);
  const moduleNames = new Map((catalog?.modules || []).map((mod) => [mod.code, mod.name]));
  const overrides = settings.rateOverrides || {};

  const subscriptionLines = [];
  const serviceLines = [];
  let incomplete = false;

  const addLine = (item, spec) => {
    const overridden = hasOverride(overrides, item.code);
    const rate = overridden ? clampSharePercent(overrides[item.code]) : spec.defaultRate;
    const basisAmount = spec.basisAmount;
    if (basisAmount == null) incomplete = true;
    const shareAmount = lineShare(basisAmount, rate);
    const payableRate = basisAmount == null || shareAmount == null
      ? null
      : clampSharePercent(100 - rate);
    const payableAmount = basisAmount == null || shareAmount == null
      ? null
      : roundMoney(Number(basisAmount) - Number(shareAmount));
    const renewalShare = spec.renews ? shareAmount : 0;
    let renewalPayable = 0;
    let renewalBasis = 0;
    if (basisAmount != null && spec.kind !== 'one_time') {
      renewalBasis = basisAmount;
      renewalPayable = spec.renews ? payableAmount : basisAmount;
    }
    const line = {
      code: item.code,
      kind: spec.kind,
      kindLabel: spec.kindLabel,
      name: item.name,
      basisAmount,
      rate,
      defaultRate: spec.defaultRate,
      overridden,
      shareAmount,
      payableRate,
      payableAmount,
      renews: spec.renews,
      renewalShare,
      renewalPayable,
      renewalBasis,
      note: overridden ? `${spec.note ? `${spec.note}；` : ''}已手動調整` : spec.note
    };
    if (spec.kind === 'subscription') subscriptionLines.push(line);
    else serviceLines.push(line);
  };

  for (const item of quote?.subscriptionItems || []) {
    const monthly = item.quotedAmount == null || item.quotedAmount === ''
      ? null
      : Number(item.quotedAmount);
    const basisAmount = monthly == null || Number.isNaN(monthly)
      ? null
      : roundMoney(monthly * (Number(item.chargeMonths) || priceMonths));
    const moduleCode = moduleCodeFromItem(item.code);
    const noShare = Boolean(moduleCode && NO_SHARE_MODULE_CODES.includes(moduleCode));
    const fixedAiShare = Boolean(moduleCode && aiModules.has(moduleCode));
    const baseRate = referral ? settings.referralRate : subscriptionRate;
    let defaultRate = baseRate;
    let renews = !referral;
    const notes = [];
    if (noShare) {
      defaultRate = 0;
      notes.push('實收全額付給平台');
    } else if (fixedAiShare && settings.enterpriseDeal === 'referral') {
      defaultRate = settings.referralRate;
      renews = false;
      notes.push('引薦模式，與介紹費相同');
    } else if (fixedAiShare) {
      defaultRate = AI_MODULE_SHARE_RATE;
      renews = true;
      notes.push('固定分潤 25%，經銷價為售價的 75%');
    } else if (referral) {
      notes.push('首年只留介紹費，其餘付給平台');
    }
    if (item.noYearlyDiscount && Number(item.chargeMonths) !== priceMonths) {
      notes.push('年繳不含兩個月優惠');
    }
    addLine(item, {
      kind: 'subscription',
      kindLabel: '訂閱',
      basisAmount,
      defaultRate,
      renews,
      note: notes.join('；')
    });
  }

  for (const item of quote?.serviceFeeItems || []) {
    const raw = item.quotedAmount == null || item.quotedAmount === '' ? null : Number(item.quotedAmount);
    const basisAmount = raw == null || Number.isNaN(raw) ? null : roundMoney(raw);
    const code = String(item.code || '');
    let defaultRate = 0;
    let note = '實收全額付給平台';
    let renews = false;

    if (SETUP_SHARE_CODES.includes(code)) {
      const enterpriseSetupInReferral = referral && code === 'setup_fee';
      if (enterpriseSetupInReferral) {
        defaultRate = settings.referralRate;
        note = '引薦模式，企業版建置費與介紹費相同';
      } else if (settings.setupExecutor === 'dealer') {
        defaultRate = settings.setupDealerRate;
        note = '經銷商自行執行導入／建置';
      } else {
        defaultRate = subscriptionRate;
        note = '原廠執行導入／建置，依訂閱級距計算應付平台';
      }
    } else if (code === 'sms_points') {
      note = '簡訊實收全額付給平台';
    } else if (code === 'ai_credit_topup') {
      if (settings.enterpriseDeal === 'referral') {
        defaultRate = settings.referralRate;
        note = '引薦模式，與介紹費相同';
      } else {
        defaultRate = AI_MODULE_SHARE_RATE;
        note = 'AI 儲值固定分潤 25%，經銷價為售價的 75%';
      }
    } else if (item.kind === 'annual') {
      note = '年費實收全額付給平台';
      renews = true;
    }

    addLine(item, {
      kind: item.kind === 'annual' ? 'annual' : 'one_time',
      kindLabel: item.kind === 'annual' ? '年費' : '單次',
      basisAmount,
      defaultRate,
      renews,
      note
    });
  }

  const firstYear = { total: 0 };
  const renewal = { total: 0 };
  const subscriptionBasis = { total: 0 };
  const serviceBasis = { total: 0 };
  const firstYearBasis = { total: 0 };
  const renewalBasis = { total: 0 };
  const renewalPayable = { total: 0 };
  const firstYearPayable = { total: 0 };
  const subscriptionPayable = { total: 0 };
  const servicePayable = { total: 0 };
  const subscriptionShare = { total: 0 };
  const serviceShare = { total: 0 };
  for (const line of subscriptionLines) {
    pushShare(subscriptionShare, line.shareAmount);
    pushShare(subscriptionPayable, line.payableAmount);
    pushShare(subscriptionBasis, line.basisAmount);
    pushShare(firstYear, line.shareAmount);
    pushShare(firstYearPayable, line.payableAmount);
    pushShare(firstYearBasis, line.basisAmount);
    if (line.renews) pushShare(renewal, line.renewalShare);
    pushShare(renewalPayable, line.renewalPayable);
    pushShare(renewalBasis, line.renewalBasis);
  }
  for (const line of serviceLines) {
    pushShare(serviceShare, line.shareAmount);
    pushShare(servicePayable, line.payableAmount);
    pushShare(serviceBasis, line.basisAmount);
    pushShare(firstYear, line.shareAmount);
    pushShare(firstYearPayable, line.payableAmount);
    pushShare(firstYearBasis, line.basisAmount);
    if (line.renews) pushShare(renewal, line.renewalShare);
    pushShare(renewalPayable, line.renewalPayable);
    pushShare(renewalBasis, line.renewalBasis);
  }

  const payRate = (share) => clampSharePercent(100 - share);
  const tierLabel = settings.tier === 'advanced'
    ? '進階'
    : settings.tier === 'custom'
      ? '自訂'
      : '一般';
  const aiNames = AI_TOKEN_COST_MODULE_CODES
    .map((code) => moduleNames.get(code) || code);
  const parameterSummary = [
    referral
      ? `首年訂閱應付平台 ${payRate(settings.referralRate)}%（介紹費 ${settings.referralRate}%），次年訂閱全額付給平台`
      : `訂閱應付平台 ${payRate(subscriptionRate)}%（${tierLabel}，經銷商留 ${subscriptionRate}%）`,
    settings.setupExecutor === 'dealer'
      ? `導入／建置費應付平台 ${payRate(settings.setupDealerRate)}%（自行執行）`
      : `導入／建置費應付平台 ${payRate(subscriptionRate)}%（原廠執行）`,
    settings.enterpriseDeal === 'referral'
      ? `${aiNames.join('、')}、企業版建置費與 AI 儲值同為引薦介紹費 ${settings.referralRate}%`
      : `${aiNames.join('、')}與 AI 儲值固定分潤 ${AI_MODULE_SHARE_RATE}%，經銷價為售價的 ${100 - AI_MODULE_SHARE_RATE}%`
  ].join('｜');

  const footnotes = [
    '本報價單由平台開立給經銷商，請勿轉交終端客戶。經銷商向客戶收取未稅金額後，按表列金額付給平台。',
    '應付平台 = 客戶實收 − 經銷商分潤，不含營業稅。',
    `訂閱依本約實收估算（${quote?.billingCycle?.name || '所選週期'}，月費 × ${priceMonths}）。`,
    '簡訊、內容上架、行銷代操，以及導入／建置費以外的一次性費用，預設實收全額付給平台。',
    '非引薦時，ChatMate、ChatDesk 與 AI 儲值分潤固定 25%，經銷價為售價的 75%。引薦模式時 ChatMate、ChatDesk、企業版建置費與 AI 儲值改與介紹費相同。ChatDesk 加購座席也跟著介紹費。',
    '經銷商自行執行導入／建置時，預設留全額，不必再付給平台。'
  ];
  if (referral) {
    footnotes.push('企業版引薦、原廠成交：首年訂閱只留介紹費，其餘付給平台；次年訂閱實收全額付給平台。');
  }

  return {
    settings,
    priceMonths,
    referral,
    subscriptionLines,
    serviceLines,
    lines: [...subscriptionLines, ...serviceLines],
    subscriptionShare: subscriptionShare.total,
    serviceShare: serviceShare.total,
    subscriptionPayable: subscriptionPayable.total,
    servicePayable: servicePayable.total,
    subscriptionBasis: subscriptionBasis.total,
    serviceBasis: serviceBasis.total,
    firstYearBasis: firstYearBasis.total,
    renewalBasis: renewalBasis.total,
    firstYearShare: firstYear.total,
    renewalShare: renewal.total,
    firstYearPayable: firstYearPayable.total,
    renewalPayable: renewalPayable.total,
    incomplete,
    parameterSummary,
    footnotes
  };
}
